let _ = t => t,
  _t;
import { c as _c } from "react-compiler-runtime";
import { useCallback, useRef } from 'react';
import styledWithTheme from 'foundations-theming/utils';
import { useObserveTableElementRef } from '../../virtualization/hooks/useObserveTableElementRef';
import { LEFT_POSITION, RIGHT_POSITION, useColumnDragAndDrop } from '../../reordering/useColumnDragAndDrop';
import { COLUMN } from '../../virtualization/types/observerTypes';
import ContentComponentInner from './ContentComponentInner';
import { useFDTContextMenuConfig } from '../../contextMenu/context/FDTContextMenuConfigContext';
import { jsx as _jsx } from "react/jsx-runtime";
const StyledDropTarget = styledWithTheme.div(_t || (_t = _`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;

  border-width: ${0};
  border-style: none;
  border-radius: ${0};
  border-color: ${0};

  && [data-hide-when-not-hovered='true'] {
    display: none;
  }
  th:hover && [data-hide-when-not-hovered='true'],
  th:focus-within && [data-hide-when-not-hovered='true'] {
    display: block;
  }
`), ({
  theme
}) => theme.borderWidth[200], ({
  theme
}) => theme.borderRadius[300], ({
  theme,
  $isActiveDropTarget
}) => $isActiveDropTarget ? theme.color['border-core-hover'] : `transparent`);

/** This component is what is passed to UITable's `Content` prop, and internally implements column header drag and drop, and resizing
 * ResizableSortTH
 *   getContentComponent
 *     DraggableResizableHeaderContent
 *       Children
 *       ContentComponentInner
 *         ContextMenuHeaderTriggerButton
 *         ResizeAffordance
 */
export const DraggableResizableHeaderContent = t0 => {
  const $ = _c(43);
  const {
    arrowsNode,
    children,
    columnIndex,
    columnName,
    columnNamespace,
    reorderable,
    onColumnOrderChange,
    handleResize,
    originalWidth,
    onResizeEnd,
    resizable,
    sortDirection
  } = t0;
  const initialXRef = useRef(0);
  const handleObserveRef = useObserveTableElementRef();
  let t1;
  if ($[0] !== columnIndex || $[1] !== columnName || $[2] !== columnNamespace || $[3] !== onColumnOrderChange || $[4] !== reorderable) {
    t1 = {
      columnName,
      columnNamespace,
      columnIndex,
      onColumnOrderChange,
      reorderable
    };
    $[0] = columnIndex;
    $[1] = columnName;
    $[2] = columnNamespace;
    $[3] = onColumnOrderChange;
    $[4] = reorderable;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const {
    onDragStart,
    onDragEnter,
    onDragLeave,
    onDrop,
    onDragOver,
    onDrag,
    isActiveDropTarget,
    dropPosition
  } = useColumnDragAndDrop(t1);
  const contextMenuConfig = useFDTContextMenuConfig();
  let t2;
  if ($[6] !== columnName || $[7] !== columnNamespace || $[8] !== contextMenuConfig) {
    t2 = contextMenuConfig === null || contextMenuConfig === void 0 ? void 0 : contextMenuConfig.getIsContextMenuOpen({
      row: null,
      columnName,
      columnNamespace
    });
    $[6] = columnName;
    $[7] = columnNamespace;
    $[8] = contextMenuConfig;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const isContextMenuOpen = t2;
  const isResizingRef = useRef(false);
  let t3;
  if ($[10] !== handleResize || $[11] !== onResizeEnd || $[12] !== originalWidth) {
    t3 = initiatingEvent => {
      initialXRef.current = initiatingEvent.clientX;
      isResizingRef.current = true;
      initiatingEvent.preventDefault();
      const handleMouseMove = t4 => {
        const {
          clientX
        } = t4;
        return handleResize(originalWidth + (clientX - initialXRef.current));
      };
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", event => {
        event.preventDefault();
        document.removeEventListener("mousemove", handleMouseMove);
        onResizeEnd();
        requestAnimationFrame(() => {
          isResizingRef.current = false;
        });
      }, {
        once: true
      });
    };
    $[10] = handleResize;
    $[11] = onResizeEnd;
    $[12] = originalWidth;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const handleMouseDown = t3;
  let t4;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = event_0 => {
      if (isResizingRef.current) {
        event_0.stopPropagation();
      }
    };
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const handleClick = t4;
  let t5;
  if ($[15] !== columnName || $[16] !== columnNamespace || $[17] !== reorderable) {
    t5 = reorderable && {
      "data-test-id": `drag-header-${columnNamespace}-${columnName}`
    };
    $[15] = columnName;
    $[16] = columnNamespace;
    $[17] = reorderable;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const t6 = reorderable && !isContextMenuOpen;
  const t7 = isActiveDropTarget && dropPosition === LEFT_POSITION;
  const t8 = isActiveDropTarget && dropPosition === RIGHT_POSITION;
  let t9;
  if ($[19] !== arrowsNode || $[20] !== children || $[21] !== columnName || $[22] !== columnNamespace || $[23] !== handleMouseDown || $[24] !== reorderable || $[25] !== resizable || $[26] !== sortDirection || $[27] !== t7 || $[28] !== t8) {
    t9 = /*#__PURE__*/_jsx(ContentComponentInner, {
      arrowsNode: arrowsNode,
      columnName: columnName,
      columnNamespace: columnNamespace,
      onMouseDown: handleMouseDown,
      resizable: resizable,
      reorderable: reorderable,
      draggedLeft: t7,
      draggedRight: t8,
      sortDirection: sortDirection,
      children: children
    });
    $[19] = arrowsNode;
    $[20] = children;
    $[21] = columnName;
    $[22] = columnNamespace;
    $[23] = handleMouseDown;
    $[24] = reorderable;
    $[25] = resizable;
    $[26] = sortDirection;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== columnIndex || $[31] !== handleObserveRef || $[32] !== isActiveDropTarget || $[33] !== onDrag || $[34] !== onDragEnter || $[35] !== onDragLeave || $[36] !== onDragOver || $[37] !== onDragStart || $[38] !== onDrop || $[39] !== t5 || $[40] !== t6 || $[41] !== t9) {
    t10 = /*#__PURE__*/_jsx(StyledDropTarget, Object.assign({
      $isActiveDropTarget: isActiveDropTarget
    }, t5, {
      ref: handleObserveRef,
      "data-observer-type": COLUMN,
      "data-observer-id": columnIndex,
      draggable: t6,
      onClick: handleClick,
      onDragStart: onDragStart,
      onDrop: onDrop,
      onDragEnter: onDragEnter,
      onDragLeave: onDragLeave,
      onDragOver: onDragOver,
      onDrag: onDrag,
      children: t9
    }));
    $[30] = columnIndex;
    $[31] = handleObserveRef;
    $[32] = isActiveDropTarget;
    $[33] = onDrag;
    $[34] = onDragEnter;
    $[35] = onDragLeave;
    $[36] = onDragOver;
    $[37] = onDragStart;
    $[38] = onDrop;
    $[39] = t5;
    $[40] = t6;
    $[41] = t9;
    $[42] = t10;
  } else {
    t10 = $[42];
  }
  return t10;
};